import { RootStore } from 'store/rootStore';
import { makeAutoObservable, runInAction, toJS } from 'mobx';
import { EnrollmentPeriod, EnrollmentType, Proposal } from 'types/enrollmentService';
import { brokerRights } from '@insurancemenu/imenu-components-lib/dist/utils/accessRights';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { getAllEnrollmentPeriodsByGroupId, markAsPreferred } from 'api/operations/enrollmentService';

export interface GroupProposalPerPeriod {
  proposal: Proposal;
  enrollmentPeriod: EnrollmentPeriod;
}

interface CurrentProposal {
  enrollmentPeriodId: string;
  cartId: string;
}

const INITIAL_CURRENT_PROPOSAL: Readonly<CurrentProposal> = Object.freeze({
  enrollmentPeriodId: '',
  cartId: '',
});

type ConfirmationModalType = 'DELETE_PROPOSAL' | 'MARK_AS_PREFERRED';

export class GroupProposalsTableStore {
  rootStore: RootStore;
  groupId = '';
  groupProposalsPerPeriod: GroupProposalPerPeriod[] = [];
  loading = false;
  currentProposal: CurrentProposal = {
    ...INITIAL_CURRENT_PROPOSAL,
  };

  allEnrollmentPeriods: EnrollmentPeriod[] = [];
  showPlanDetailsModal = false;
  showProductDetailsModal = false;

  // Confirmation Modal
  showConfirmationModal = false;
  confirmationModalType: ConfirmationModalType | null = null;

  public openConfirmationModal(type: ConfirmationModalType, currentProposal: CurrentProposal): void {
    this.showConfirmationModal = true;
    this.confirmationModalType = type;
    this.currentProposal = currentProposal;
  }

  public closeConfirmationModal(): void {
    this.showConfirmationModal = false;
    this.confirmationModalType = null;
    this.currentProposal = { ...INITIAL_CURRENT_PROPOSAL };
  }

  public buildProposalsList(groupId: string): void {
    this.groupId = groupId;
    this.groupProposalsPerPeriod = [];
    this.allEnrollmentPeriods.forEach((enrollmentPeriod) => {
      enrollmentPeriod.proposals.forEach((proposal) => {
        this.groupProposalsPerPeriod.push({
          proposal,
          enrollmentPeriod,
        });
      });
    });
    this.groupProposalsPerPeriod.sort((a, b) => b.proposal.name.localeCompare(a.proposal.name));
  }

  public async updateProposalList(groupId: string): Promise<void> {
    this.loading = true;
    const result = await getAllEnrollmentPeriodsByGroupId(this.groupId);
    runInAction(() => {
      if (result) {
        this.allEnrollmentPeriods = result;
        this.buildProposalsList(groupId);
      }
      this.loading = false;
    });
  }

  public async deleteProposal(enrollmentPeriodId: string, cartId: string): Promise<void> {
    this.loading = true;
    await this.rootStore.enrollmentStore.deleteProposal(enrollmentPeriodId, cartId);
    runInAction(() => {
      this.groupProposalsPerPeriod = this.groupProposalsPerPeriod.filter(
        (groupProposalPerPeriod) => groupProposalPerPeriod.proposal.cartId !== cartId,
      );
      this.loading = false;
    });
  }

  public async markAsPreferred(enrollmentPeriodId: string, cartId: string): Promise<void> {
    this.loading = true;
    await markAsPreferred(enrollmentPeriodId, cartId);
    runInAction(() => {
      void this.updateProposalList(enrollmentPeriodId);
      this.loading = false;
    });
  }

  public async editProposalV2(cartId: string): Promise<void> {
    this.loading = true;
    const result = await this.rootStore.cartStoreV2.copyCart(cartId);
    runInAction(() => {
      if (!result.data) {
        throw new Error(`Copy cart wasn't successful`);
      }
      const selectedProductLineId = this.findEpByProposalId(cartId).productLines[0].id;
      window.location.href = this.v2ProposalRedirect(selectedProductLineId, result.data.copyCart.id);
    });
  }

  public v2ProposalRedirect(productLineId: ProductLineId, cartId: string): string {
    return `/browsing?groupId=${this.groupId}&productLineId=${productLineId}&cartId=${cartId}`;
  }

  // If the Proposal was made for previous Domain version, disable ability to Edit it
  // If the user is not a Broker, it cannot Edit Proposal
  // If the Sponsor doesn't allow to change active (renewal) plan, we should hide button
  // Edit Proposal button is available only for Small business proposal
  public isEditProposalAvailable(cartId: string): boolean {
    const convertedUserRoles = this.rootStore.userStore.userRoles.map((role) => toJS(role)).slice();
    const periodByProposalId = this.getPeriodByProposalId(cartId);
    const mutableRenewal =
      this.rootStore.domainStoreV2.isChangePlanActionAvailable || periodByProposalId.type !== EnrollmentType.RENEWAL;
    return periodByProposalId.rateSegment === 'GROUP' && mutableRenewal && brokerRights(convertedUserRoles);
  }

  public findEpByProposalId(cartId: string): Proposal {
    const proposal = this.groupProposalsPerPeriod.find(
      (proposalPerPeriod) => proposalPerPeriod.proposal.cartId === cartId,
    )?.proposal;
    if (proposal) {
      return proposal;
    }
    throw new Error(`Cannot find proposal for such id: ${cartId}`);
  }

  private getPeriodByProposalId(cartId: string): EnrollmentPeriod {
    const epSearch = this.groupProposalsPerPeriod
      .filter((item) => item.proposal.cartId === cartId)
      .flatMap((item) => item.enrollmentPeriod);
    if (epSearch.length === 1) {
      return epSearch[0];
    }
    throw new Error(`Enrollment Period for such cart id ${cartId} doesn't exist`);
  }

  public setShowPlanDetailsModal(value: boolean): void {
    this.showPlanDetailsModal = value;
  }

  public setShowProductDetailsModal(value: boolean): void {
    this.showProductDetailsModal = value;
  }

  public getPeriodsLocally(periods: EnrollmentPeriod[]): void {
    this.allEnrollmentPeriods = periods;
  }

  public setLoading(loading: boolean): void {
    this.loading = loading;
  }

  public resetProposalsData(): void {
    this.groupProposalsPerPeriod = [];
    this.rootStore.enrollmentStore.setEnrollmentPeriods([]);
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
